import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  CONDITION_NOTE_MAX,
  countSession,
  precheckHandover,
  viewScan,
  waitingDays,
  type AddScanResult,
  type OpsSession,
  type ScanTone,
} from '@ecobox/firebase';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  ScanInput,
  ScannerView,
  SignaturePad,
  Textarea,
  TimeInState,
  scanFeedback,
  useNow,
  useToast,
  type BadgeTone,
  type ScanVerdict,
} from '@ecobox/ui';
import { useHandover, useWaitingUnits } from '../hooks/useHandover';
import { useWakeLock } from '../hooks/useWakeLock';
import { loadBarcodeDetector } from '../scanner/detector';
import { dataUrlToBlob } from '../session/files';
import { useServices, useSyncState } from '../services';
import { useAllSessions, useLedgerById, useSessionEngine } from '../sessions';

const TONE_BADGE: Record<ScanTone, BadgeTone> = {
  ok: 'success',
  wait: 'warning',
  warn: 'warning',
  bad: 'danger',
};
const VERDICT: Record<AddScanResult['tone'], ScanVerdict> = { ok: 'ok', warn: 'warn', bad: 'bad' };
/** The most units one handover carries (`MAX_UNITS_PER_HANDOVER` in the domain). */
const MAX_UNITS = 400;
const PAGE = 100;

export function HandoverSessionPage() {
  const { id } = useParams();
  const session = useAllSessions().find((s) => s.id === id);
  if (!session || session.kind !== 'load_handover') {
    return (
      <EmptyState
        title="This handover is not on this phone"
        description="It may have been finished, or it was started on another phone."
        action={
          <Link to="/handover">
            <Button size="touch">Back to Hand over</Button>
          </Link>
        }
      />
    );
  }
  return <HandoverScreen session={session} />;
}

function HandoverScreen({ session }: { session: OpsSession }) {
  const engine = useSessionEngine();
  const ledger = useLedgerById();
  const navigate = useNavigate();
  const finished = session.finishedAt !== undefined;
  const [stage, setStage] = useState<'scan' | 'sign'>('scan');
  const handover = useHandover(session.targetId);
  const partnerId = session.extra?.partnerId ?? handover.data?.partnerId ?? '';
  const waiting = useWaitingUnits(finished ? null : session.branchId);
  const [camera, setCamera] = useState(false);
  const [last, setLast] = useState<AddScanResult | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE);
  const toast = useToast();
  const now = useNow();

  useWakeLock(!finished);

  const counts = countSession(session, ledger);
  const units = waiting.byPartner.get(partnerId) ?? [];
  const scanned = new Set(session.scans.map((s) => s.barcode));
  const todo = units.filter((u) => !scanned.has(u.unitId));
  const ready = todo.filter((u) => !u.problem);
  const damaged = session.scans.filter((s) => s.note).length;

  const handleScan = (raw: string): ScanVerdict => {
    const barcode = raw.trim().toUpperCase();
    const precheck = precheckHandover({
      barcode,
      box: waiting.boxes.get(barcode),
      branchId: session.branchId,
      partnerId,
      waiting: waiting.byPartner,
    });
    if (session.scans.length >= MAX_UNITS && !scanned.has(barcode)) {
      const full: AddScanResult = {
        outcome: 'invalid',
        barcode,
        message: `A handover carries at most ${MAX_UNITS} units. Sign this one and start another.`,
        tone: 'bad',
      };
      setLast(full);
      return 'bad';
    }
    const result = engine.addScan(session.id, barcode, precheck);
    setLast(result);
    return VERDICT[result.tone];
  };

  const addAllWaiting = () => {
    let added = 0;
    for (const unit of ready.slice(0, Math.max(0, MAX_UNITS - session.scans.length))) {
      if (engine.addScan(session.id, unit.unitId).outcome === 'added') added += 1;
    }
    toast.show({
      title: 'Added',
      message: `${added} unit${added === 1 ? '' : 's'} added from the waiting list.`,
      tone: 'info',
    });
  };

  const saveNote = (barcode: string, note: string) => {
    if (!engine.setNote(session.id, barcode, note)) {
      toast.show({
        title: 'Note not saved',
        message: 'This unit was already sent. Add damage notes before you finish scanning.',
        tone: 'danger',
      });
    }
    setEditing(null);
  };

  if (finished) return <SignedView session={session} onBack={() => void navigate('/handover')} />;

  const rows = [...session.scans].reverse();

  return (
    <div className="ops-stack">
      <div>
        <h1 className="ops-title">Hand over</h1>
        <p className="ops-muted" style={{ margin: 0 }}>
          {session.targetLabel} · started <TimeInState since={session.startedAt} suffix="ago" />
          {handover.data ? ` · ${handover.data.status.replace(/_/g, ' ')}` : ''}
        </p>
      </div>

      <div className="ops-tiles" data-tour="handover-counters">
        <Tile n={counts.good} label="scanned" tone="ok" />
        <Tile
          n={counts.unsent}
          label="not sent yet"
          tone={counts.unsent > 0 ? 'wait' : undefined}
        />
        {damaged > 0 ? <Tile n={damaged} label="damaged" tone="wait" /> : null}
        {counts.held > 0 ? <Tile n={counts.held} label="held back" tone="bad" /> : null}
        {counts.refused > 0 ? <Tile n={counts.refused} label="refused" tone="bad" /> : null}
      </div>

      {stage === 'scan' ? (
        <>
          {todo.length > 0 ? (
            <Card title="Waiting for this partner" data-tour="handover-waiting">
              <div className="ops-stack">
                <p style={{ margin: 0 }}>
                  {ready.length} box{ready.length === 1 ? '' : 'es'} ready
                  {todo.length > ready.length
                    ? `, ${todo.length - ready.length} not allowed yet (${todo
                        .filter((u) => u.problem)
                        .slice(0, 3)
                        .map((u) => u.problem)
                        .join('; ')})`
                    : ''}
                  . Oldest has waited {waitingDays(todo[0]?.since ?? '', now)} day(s).
                </p>
                <Button
                  size="touch"
                  variant="secondary"
                  block
                  disabled={ready.length === 0}
                  onClick={addAllWaiting}
                >
                  Add all {ready.length} ready boxes
                </Button>
                <p className="ops-muted" style={{ margin: 0 }}>
                  Or scan the boxes as they go on the vehicle. Scanning is how you check each one.
                </p>
              </div>
            </Card>
          ) : waiting.error ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              The list of what is waiting is not available. Scan the boxes one by one; the server
              checks each one.
            </p>
          ) : null}

          <div data-tour="scan-field">
            <ScanInput
              onScan={(code) => scanFeedback(handleScan(code))}
              label="Scan or type a box barcode or piece label"
              status={
                last ? (last.tone === 'bad' ? 'error' : last.tone === 'ok' ? 'ok' : 'idle') : 'idle'
              }
            />
          </div>
          {last ? (
            <div className="ops-last" data-tone={last.tone} role="status">
              <span className="ops-code">{last.barcode}</span> {last.message}
            </div>
          ) : null}
          <Button
            size="touch"
            variant="cta"
            block
            data-tour="scan-camera"
            onClick={() => setCamera(true)}
          >
            Scan with the camera
          </Button>
        </>
      ) : (
        <SignStep
          session={session}
          partnerName={handover.data?.partnerName ?? session.targetLabel}
          onBack={() => setStage('scan')}
        />
      )}

      {session.lastError ? (
        <p role="alert" className="ops-error">
          {session.lastError}
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p className="ops-muted" data-tour="scan-list">
          Nothing scanned yet. Scan a box and it shows here at once.
        </p>
      ) : (
        <ul className="ops-list" aria-label="Scans" data-tour="scan-list">
          {rows.slice(0, shown).map((scan) => {
            const view = viewScan(scan, scan.commandId ? ledger.get(scan.commandId) : undefined);
            const canNote = scan.status === 'buffered' || scan.status === 'held';
            return (
              <li
                key={scan.barcode}
                className="ops-row"
                data-tone={view.tone === 'wait' ? 'waiting' : view.tone}
              >
                <div className="ops-row__top">
                  <span className="ops-code">{scan.barcode}</span>
                  <Badge tone={TONE_BADGE[view.tone]}>{view.label}</Badge>
                </div>
                {view.detail ? <div className="ops-detail">{view.detail}</div> : null}
                {scan.note ? <div className="ops-detail">Damage noted: {scan.note}</div> : null}
                {editing === scan.barcode ? (
                  <NoteEditor
                    initial={scan.note ?? ''}
                    onSave={(note) => saveNote(scan.barcode, note)}
                    onCancel={() => setEditing(null)}
                  />
                ) : (
                  <div className="ops-row__actions">
                    {stage === 'scan' && canNote ? (
                      <Button
                        size="lg"
                        variant="secondary"
                        onClick={() => setEditing(scan.barcode)}
                      >
                        {scan.note ? 'Change damage note' : 'Note damage'}
                      </Button>
                    ) : null}
                    {scan.status === 'held' ? (
                      <Button
                        size="lg"
                        variant="secondary"
                        onClick={() => engine.releaseHeld(session.id, scan.barcode)}
                      >
                        Send anyway
                      </Button>
                    ) : null}
                    {stage === 'scan' &&
                    (scan.status === 'held' ||
                      scan.status === 'invalid' ||
                      scan.status === 'buffered' ||
                      scan.status === 'queued') ? (
                      <Button
                        size="lg"
                        variant="ghost"
                        onClick={() => engine.removeScan(session.id, scan.barcode)}
                      >
                        {scan.status === 'queued' ? 'Take off' : 'Remove'}
                      </Button>
                    ) : null}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {rows.length > shown ? (
        <Button size="lg" variant="secondary" onClick={() => setShown(shown + PAGE)}>
          Show more ({rows.length - shown} older)
        </Button>
      ) : null}

      {stage === 'scan' ? (
        <div className="ops-actionbar" data-tour="finish-sign">
          <Button
            size="touch"
            variant="primary"
            block
            disabled={counts.good === 0}
            onClick={() => {
              // The notes travel with this batch: nothing was sent before.
              engine.flush(session.id);
              setStage('sign');
            }}
          >
            Finish scanning and sign
          </Button>
          {rows.length === 0 ? (
            <Button
              size="lg"
              variant="ghost"
              onClick={() => {
                if (engine.discard(session.id)) void navigate('/handover');
              }}
            >
              Cancel this handover
            </Button>
          ) : null}
        </div>
      ) : null}

      {camera ? (
        <ScannerView
          loadDetector={loadBarcodeDetector}
          title={`Hand over · ${session.targetLabel}`}
          count={counts.good}
          countLabel="scanned"
          lastResult={
            last ? { code: last.barcode, tone: VERDICT[last.tone], message: last.message } : null
          }
          onRead={handleScan}
          onClose={() => setCamera(false)}
        />
      ) : null}
    </div>
  );
}

function Tile({ n, label, tone }: { n: number; label: string; tone?: ScanTone | undefined }) {
  return (
    <div className="ops-tile" data-tone={tone}>
      <div className="ops-tile__n">{n}</div>
      <div className="ops-muted">{label}</div>
    </div>
  );
}

function NoteEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (note: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initial);
  return (
    <div className="ops-stack">
      <Field
        label="What is wrong with this box?"
        hint="The partner's representative sees this before signing."
      >
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={text}
            maxLength={CONDITION_NOTE_MAX}
            onChange={(e) => setText(e.target.value)}
          />
        )}
      </Field>
      <div className="ops-row__actions">
        <Button size="lg" variant="primary" onClick={() => onSave(text)}>
          Save note
        </Button>
        {initial ? (
          <Button size="lg" variant="ghost" onClick={() => onSave('')}>
            Clear note
          </Button>
        ) : null}
        <Button size="lg" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function SignStep({
  session,
  partnerName,
  onBack,
}: {
  session: OpsSession;
  partnerName: string;
  onBack: () => void;
}) {
  const engine = useSessionEngine();
  const { writer, uploads } = useServices();
  const ledger = useLedgerById();
  const online = useSyncState().online;
  const toast = useToast();
  const counts = countSession(session, ledger);
  const loaded = counts.good;
  const checking = online && counts.waiting > 0;

  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  // The count follows the units (a refusal can lower it) until the person types their own.
  const [typed, setTyped] = useState<string | null>(null);
  const agreedText = typed ?? String(loaded);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const agreed = /^\d+$/.test(agreedText.trim()) ? Number(agreedText) : undefined;

  const refused = session.scans.flatMap((s) => {
    const view = viewScan(s, s.commandId ? ledger.get(s.commandId) : undefined);
    return view.counts || view.tone !== 'bad'
      ? []
      : [{ barcode: s.barcode, why: view.detail ?? view.label }];
  });

  const submit = async () => {
    setError(null);
    if (name.trim() === '') return setError('Write the name of the partner’s representative.');
    if (!signature) return setError('The representative signs on the pad.');
    if (agreed === undefined || agreed <= 0)
      return setError('Write the number of units the representative counted.');
    if (agreed !== loaded) {
      return setError(
        `The representative counted ${agreed} but ${loaded} unit${loaded === 1 ? ' is' : 's are'} loaded. Count again together.`,
      );
    }
    setBusy(true);
    try {
      // Pictures are saved on the phone first and upload when there is signal; the command names
      // where they will be, so the handover does not wait for the network.
      const signaturePath = await uploads.enqueueUpload(dataUrlToBlob(signature), {
        kind: 'signature',
      });
      const vehiclePhotoPath = photo ? await uploads.enqueueUpload(photo, { kind: 'vehicle' }) : '';
      const sent = writer.send('handoverToPartner', {
        handoverId: session.targetId,
        representativeName: name.trim(),
        plate: plate.trim().toUpperCase(),
        signaturePath,
        vehiclePhotoPath,
        agreedCount: agreed,
      });
      engine.markClosed(session.id, sent.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.show({ title: 'Not signed', message: 'Nothing was sent. Try again.', tone: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={`Sign for ${partnerName}`}>
      <div className="ops-stack">
        <p style={{ margin: 0 }}>
          {loaded} unit{loaded === 1 ? '' : 's'} to hand over
          {counts.refused > 0 ? `, ${counts.refused} refused` : ''}
          {session.scans.filter((s) => s.note).length > 0
            ? `, ${session.scans.filter((s) => s.note).length} noted as damaged`
            : ''}
          .
        </p>
        {checking ? (
          <p role="status" className="ops-muted" style={{ margin: 0 }}>
            The server is checking {counts.waiting} unit{counts.waiting === 1 ? '' : 's'}…
          </p>
        ) : null}
        {refused.length > 0 ? (
          <div role="alert" className="ops-last" data-tone="bad">
            <strong>
              {refused.length} unit{refused.length === 1 ? ' was' : 's were'} not accepted and stay
              here:
            </strong>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {refused.slice(0, 5).map((r) => (
                <li key={r.barcode}>
                  <span className="ops-code">{r.barcode}</span> {r.why}
                </li>
              ))}
            </ul>
            {refused.length > 5 ? (
              <div>…and {refused.length - 5} more (see the list below).</div>
            ) : null}
          </div>
        ) : null}
        <Field label="Representative's name" required>
          {(p) => (
            <Input
              {...p}
              size="touch"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Vehicle plate number">
          {(p) => (
            <Input
              {...p}
              size="touch"
              value={plate}
              maxLength={20}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
            />
          )}
        </Field>
        <Field
          label="Photo of the loaded vehicle"
          hint="Uploads when you have signal; signing does not wait for it."
        >
          {(p) => (
            <input
              id={p.id}
              aria-describedby={p['aria-describedby']}
              className="ops-file"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            />
          )}
        </Field>
        <Field label="Units the representative counted" required>
          {(p) => (
            <Input
              {...p}
              size="touch"
              inputMode="numeric"
              value={agreedText}
              onChange={(e) => setTyped(e.target.value)}
            />
          )}
        </Field>
        <SignaturePad
          label="Representative's signature"
          value={signature}
          onChange={setSignature}
        />
        {error ? (
          <p role="alert" className="ops-error" style={{ margin: 0 }}>
            {error}
          </p>
        ) : null}
        <Button
          size="touch"
          variant="cta"
          block
          loading={busy}
          disabled={checking || loaded === 0}
          onClick={() => void submit()}
        >
          Hand over to {partnerName}
        </Button>
        <Button size="touch" variant="secondary" block onClick={onBack}>
          Keep scanning
        </Button>
      </div>
    </Card>
  );
}

function SignedView({ session, onBack }: { session: OpsSession; onBack: () => void }) {
  const engine = useSessionEngine();
  const ledger = useLedgerById();
  const close = session.closeCommandId ? ledger.get(session.closeCommandId) : undefined;
  const counts = countSession(session, ledger);
  const rejected = close?.state === 'rejected';
  return (
    <div className="ops-stack">
      <h1 className="ops-title">Hand over</h1>
      <Card title={session.targetLabel}>
        <div className="ops-stack">
          {rejected ? (
            <p role="alert" className="ops-error" style={{ margin: 0 }}>
              The handover was refused: {close?.lastError?.message ?? 'no reason was given'}.
            </p>
          ) : close?.state === 'applied' || close?.state === 'applied_with_exception' ? (
            <p role="status" className="ops-ok" style={{ margin: 0 }}>
              Handed over. {counts.good} unit{counts.good === 1 ? ' is' : 's are'} with{' '}
              {session.targetLabel} now. The office sends the manifest and the partner&rsquo;s
              update link.
            </p>
          ) : (
            <p role="status" style={{ margin: 0 }}>
              Signed and saved.{' '}
              {close?.state === 'not_delivered'
                ? 'It has not reached the server: resend it from Me.'
                : 'It goes to the server when you have signal.'}
            </p>
          )}
          {rejected ? (
            <Button
              size="touch"
              variant="primary"
              block
              onClick={() => {
                engine.reopen(session.id);
              }}
            >
              Back to the handover
            </Button>
          ) : null}
          <Button size="touch" variant="secondary" block onClick={onBack}>
            Done
          </Button>
        </div>
      </Card>
    </div>
  );
}
